import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { gpCountries, gpFade, gpStats } from './global-presence.animations';

export interface GpStat {
  id: string;
  value: number;
  suffix: string;
  label: string;
  tone: 'pink' | 'violet' | 'indigo' | 'blue';
  icon: 'globe' | 'handshake' | 'rocket' | 'star';
}

/** A pin on the map. x/y are percentages of the map's 1000 x 386 viewBox. */
export interface GpRegion {
  id: string;
  name: string;
  x: number;
  y: number;
  tone: 'pink' | 'violet' | 'indigo' | 'blue';
  hub?: boolean;
}

export interface GpCountry {
  code: string;
  name: string;
}

@Component({
  selector: 'app-global-presence',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './global-presence.component.html',
  styleUrls: ['./global-presence.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [gpFade, gpStats, gpCountries],
})
export class GlobalPresenceComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gpRoot') gpRoot?: ElementRef<HTMLElement>;
  @ViewChild('gpGrid') gpGrid?: ElementRef<HTMLElement>;

  /** Flip once when the hero scrolls into view. */
  readonly heroState = signal<'hidden' | 'shown'>('hidden');
  /** Flip once when the country grid scrolls into view. */
  readonly gridState = signal<'hidden' | 'shown'>('hidden');

  /** Counters render their target until the count-up takes over in the browser. */
  readonly counters = signal<Record<string, number>>({
    countries: 20,
    partners: 250,
    projects: 500,
    years: 10,
  });

  /** Region highlighted from the legend / pin hover; '' when nothing is active. */
  hot = '';

  readonly stats: GpStat[] = [
    { id: 'countries', value: 20, suffix: '+', label: 'Countries', tone: 'pink', icon: 'globe' },
    { id: 'partners', value: 250, suffix: '+', label: 'Business Partners', tone: 'violet', icon: 'handshake' },
    { id: 'projects', value: 500, suffix: '+', label: 'Projects Delivered', tone: 'indigo', icon: 'rocket' },
    { id: 'years', value: 10, suffix: '+', label: 'Years of Trust', tone: 'blue', icon: 'star' },
  ];

  /**
   * Pin coordinates are projected from real latitude/longitude onto the same
   * equirectangular window as `world-dotted-map.svg`
   * (lon -180..180, lat 83..-56), so markers sit on the right landmass.
   */
  readonly regions: GpRegion[] = [
    { id: 'na', name: 'North America', x: 22.22, y: 29.5, tone: 'pink' },
    { id: 'sa', name: 'South America', x: 33.89, y: 68.35, tone: 'pink' },
    { id: 'eu', name: 'Europe', x: 53.33, y: 23.74, tone: 'violet' },
    { id: 'af', name: 'Africa', x: 55.56, y: 56.83, tone: 'violet' },
    { id: 'me', name: 'Middle East', x: 62.78, y: 41.01, tone: 'indigo' },
    { id: 'in', name: 'India', x: 71.81, y: 43.53, tone: 'pink', hub: true },
    { id: 'apac', name: 'Asia Pacific', x: 82.22, y: 55.4, tone: 'blue' },
  ];

  /** Delivery arcs in the map's 1000 x 386 viewBox — mostly India hub outwards. */
  readonly arcs: Array<{ id: string; ends: string[]; d: string }> = [
    { id: 'a1', ends: ['in', 'na'], d: 'M718.1 168.1Q458.2 250.1 222.2 113.9' },
    { id: 'a2', ends: ['in', 'sa'], d: 'M718.1 168.1Q549.6 299.4 338.9 263.9' },
    { id: 'a3', ends: ['in', 'eu'], d: 'M718.1 168.1Q608.9 170.5 533.3 91.7' },
    { id: 'a4', ends: ['in', 'af'], d: 'M718.1 168.1Q648.1 229.5 555.6 219.4' },
    { id: 'a5', ends: ['in', 'me'], d: 'M718.1 168.1Q670.8 183.1 627.8 158.3' },
    { id: 'a6', ends: ['in', 'apac'], d: 'M718.1 168.1Q760.1 213.9 822.2 213.9' },
    { id: 'a7', ends: ['na', 'eu'], d: 'M222.2 113.9Q381.8 158.8 533.3 91.7' },
    { id: 'a8', ends: ['sa', 'af'], d: 'M338.9 263.9Q455.2 280.7 555.6 219.4' },
  ];

  /** Soft particles drifting over the map; positions are percentages. */
  readonly motes: Array<{ x: number; y: number; d: number }> = [
    { x: 12, y: 24, d: 0 },
    { x: 41, y: 12, d: 1.4 },
    { x: 68, y: 78, d: 0.7 },
    { x: 88, y: 30, d: 2.1 },
    { x: 30, y: 84, d: 2.8 },
  ];

  readonly countries: GpCountry[] = [
    { code: 'us', name: 'United States' },
    { code: 'ca', name: 'Canada' },
    { code: 'gb', name: 'United Kingdom' },
    { code: 'de', name: 'Germany' },
    { code: 'nl', name: 'Netherlands' },
    { code: 'ch', name: 'Switzerland' },
    { code: 'fr', name: 'France' },
    { code: 'in', name: 'India' },
    { code: 'ae', name: 'UAE' },
    { code: 'sa', name: 'Saudi Arabia' },
    { code: 'sg', name: 'Singapore' },
    { code: 'jp', name: 'Japan' },
    { code: 'au', name: 'Australia' },
    { code: 'za', name: 'South Africa' },
  ];

  private observers: IntersectionObserver[] = [];
  private timers: ReturnType<typeof setInterval>[] = [];
  private reduced = false;

  constructor(
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Decide before the first render so reduced-motion visitors never see the
    // hidden state animate away, and so SSR ships finished markup.
    if (!isPlatformBrowser(platformId)) return;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) {
      this.heroState.set('shown');
      this.gridState.set('shown');
    }
  }

  trackByRegion(_i: number, r: GpRegion): string {
    return r.id;
  }

  trackByCode(_i: number, c: GpCountry): string {
    return c.code;
  }

  trackByStat(_i: number, s: GpStat): string {
    return s.id;
  }

  trackByArc(_i: number, a: { id: string }): string {
    return a.id;
  }

  /** True while the arc touches the region the visitor is pointing at. */
  isArcHot(a: { ends: string[] }): boolean {
    return !!this.hot && a.ends.includes(this.hot);
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // SSR / prerender: ship the finished state so the markup is complete.
      this.heroState.set('shown');
      this.gridState.set('shown');
      return;
    }

    this.watch(this.gpRoot, () => {
      this.heroState.set('shown');
      this.countUp();
    });
    this.watch(this.gpGrid, () => this.gridState.set('shown'));
  }

  ngOnDestroy(): void {
    this.observers.forEach((io) => io.disconnect());
    this.observers = [];
    this.timers.forEach((t) => clearInterval(t));
    this.timers = [];
  }

  /** Run `onIn` once the element is in view — immediately if it already is. */
  private watch(ref: ElementRef<HTMLElement> | undefined, onIn: () => void): void {
    const el = ref?.nativeElement;
    if (!el) return;

    if (this.reduced || el.getBoundingClientRect().top < window.innerHeight * 0.9) {
      onIn();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.zone.run(onIn);
        io.disconnect();
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px' }
    );
    this.zone.runOutsideAngular(() => io.observe(el));
    this.observers.push(io);
  }

  /** Ticks each statistic from 0 to its target over ~900ms. */
  private countUp(): void {
    if (this.reduced) return;

    const duration = 900;
    const tick = 30;
    const steps = Math.ceil(duration / tick);
    let frame = 0;

    this.counters.set(
      this.stats.reduce<Record<string, number>>((acc, s) => ((acc[s.id] = 0), acc), {})
    );

    this.zone.runOutsideAngular(() => {
      const timer = setInterval(() => {
        frame += 1;
        // ease-out so the numbers settle instead of stopping dead
        const p = 1 - Math.pow(1 - Math.min(frame / steps, 1), 3);
        const next = this.stats.reduce<Record<string, number>>((acc, s) => {
          acc[s.id] = Math.round(s.value * p);
          return acc;
        }, {});

        this.zone.run(() => {
          this.counters.set(next);
          this.cdr.markForCheck();
        });

        if (frame >= steps) clearInterval(timer);
      }, tick);

      this.timers.push(timer);
    });
  }
}
